import * as React from 'react';
import { cn } from '../lib/utils';

type TableProps = React.ComponentPropsWithoutRef<'table'>;
type TableSectionProps = React.ComponentPropsWithoutRef<'thead'>;
type TableRowProps = React.ComponentPropsWithoutRef<'tr'>;
type TableHeadProps = React.ComponentPropsWithoutRef<'th'>;
type TableCellProps = React.ComponentPropsWithoutRef<'td'>;
type TableCaptionProps = React.ComponentPropsWithoutRef<'caption'>;

export function Table({ className, ...props }: TableProps) {
  return (
    <div className="relative w-full overflow-auto">
      <table className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  );
}

export function TableHeader({ className, ...props }: TableSectionProps) {
  return <thead className={cn('[&_tr]:border-b [&_tr]:border-border', className)} {...props} />;
}

export function TableBody({ className, ...props }: TableSectionProps) {
  return <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />;
}

export function TableFooter({ className, ...props }: TableSectionProps) {
  return (
    <tfoot
      className={cn(
        'border-t border-border bg-subtle font-medium [&>tr]:last:border-b-0',
        className
      )}
      {...props}
    />
  );
}

export function TableRow({ className, ...props }: TableRowProps) {
  return (
    <tr
      className={cn(
        'border-b border-border transition-colors hover:bg-subtle data-[state=selected]:bg-selected',
        className
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: TableHeadProps) {
  return (
    <th
      className={cn(
        'h-10 px-3 text-left align-middle text-xs font-semibold text-muted-foreground [&:has([role=checkbox])]:pr-0',
        className
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: TableCellProps) {
  return (
    <td
      className={cn('px-3 py-3 align-middle [&:has([role=checkbox])]:pr-0', className)}
      {...props}
    />
  );
}

export function TableCaption({ className, ...props }: TableCaptionProps) {
  return <caption className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />;
}

/**
 * Column sizing presets for dense admin data tables.
 *
 * Tables render with the browser's default `table-auto` layout, which steals
 * width from the most-compressible column — usually a free-text name — and
 * collapses it into an unreadable one-word-per-line stack while the rest of the
 * table still overflows. Applying these presets gives each column an
 * intentional sizing contract: text columns get a readable minimum width and
 * wrap/clamp gracefully, while compact/numeric/timestamp columns hold their
 * natural width instead of being squeezed. Horizontal scrolling then appears
 * only when the table genuinely exceeds its container.
 *
 * Apply the SAME preset to a column's `<TableHead>` and its `<TableCell>` so the
 * width contract is consistent across the header and body. For long primary
 * values, pair `tableColumn.primary` on the cell with `tableClampText` on an
 * inner element to clamp to a fixed number of lines.
 */
export const tableColumn = {
  /** Primary identifier column (name/title). Readable min width, bounded max width. */
  primary: 'min-w-[15rem] max-w-[24rem]',
  /** Secondary free-text column (e.g. owner, audience). Wraps with a modest min width. */
  text: 'min-w-[9rem] max-w-[18rem] whitespace-normal break-words',
  /** Compact content that should never wrap (badges, status, channel). */
  compact: 'whitespace-nowrap',
  /** Right-aligned numeric metrics. */
  numeric: 'text-right tabular-nums whitespace-nowrap',
  /** Date / relative-time column. */
  timestamp: 'whitespace-nowrap tabular-nums',
  /** Trailing row-action column. */
  actions: 'w-10 whitespace-nowrap',
} as const;

/** Clamp long primary-column text to two lines; pair with `tableColumn.primary`. */
export const tableClampText = 'line-clamp-2 break-words';
